import type { ChangeEvent, CSSProperties, FC, ReactNode } from 'react'

import { useId, useState } from 'react'
import { formatNumber } from '@/lib/format'
import { SectionLabel } from '@/components/generals/section-label'
import { SplitWords } from '@/components/generals/split-words'

const MIN = 1000000
const MAX = 30000000
const STEP = 500000

const millions = (value: number): string => {
    const m = value / 1000000

    return `${Number.isInteger(m) ? m : m.toFixed(1).replace('.', ',')} mill.`
}

/** Svenn's own example made interactive: what 10 to 15 % better margins are worth at your turnover. */
export const MarginCalculator: FC = (): ReactNode => {
    const id = useId()
    const [revenue, setRevenue] = useState(8000000)

    const low = revenue * 0.1
    const high = revenue * 0.15
    const fill = ((revenue - MIN) / (MAX - MIN)) * 100

    const onChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setRevenue(Number(event.target.value))
    }

    return (
        <section id='margin' className='tone-lime relative overflow-hidden py-24 md:py-36' data-header='light'>
            <div className='wrap grid grid-cols-12 gap-x-6 gap-y-14'>
                <div className='col-span-12 lg:col-span-5'>
                    <SectionLabel index='05'>Regn på det selv</SectionLabel>
                    <h2 className='t-h1 mt-6 balance' data-reveal='words'>
                        <SplitWords text='Hva er bedre marginer verdt for deg?' />
                    </h2>
                    <p className='mt-6 max-w-md text-tone-muted t-body pretty'>
                        Tilbud laget i Svenn Kalkulasjon er 10 til 15 % mer lønnsomme enn med Excel og erfaringstall i hodet. Dra i spaken og se hva det betyr for bedriften din.
                    </p>
                </div>

                <div className='col-span-12 lg:col-span-6 lg:col-start-7'>
                    <label htmlFor={id} className='flex items-baseline justify-between gap-6'>
                        <span className='t-label text-tone-muted'>Årlig omsetning</span>
                        <span className='t-h2 tabular'>{millions(revenue)} kr</span>
                    </label>

                    <input
                        id={id}
                        type='range'
                        min={MIN}
                        max={MAX}
                        step={STEP}
                        value={revenue}
                        onChange={onChange}
                        aria-valuetext={`${formatNumber(revenue)} kroner`}
                        className='margin-range mt-6 w-full'
                        style={{ '--fill': `${fill}%` } as CSSProperties}
                    />

                    <div className='mt-3 flex justify-between text-xs text-tone-muted tabular' aria-hidden='true'>
                        <span>1 mill.</span>
                        <span>10 mill.</span>
                        <span>20 mill.</span>
                        <span>30 mill.</span>
                    </div>

                    <div className='mt-14 grid grid-cols-1 gap-8 border-t border-tone-line pt-8 sm:grid-cols-2' aria-live='polite'>
                        <div>
                            <p className='t-label text-tone-muted'>Med 10 % bedre margin</p>
                            <p className='margin-result mt-3 tabular'>{formatNumber(low)}<span className='text-[0.4em] font-semibold'> kr</span></p>
                        </div>
                        <div>
                            <p className='t-label text-tone-muted'>Med 15 % bedre margin</p>
                            <p className='margin-result mt-3 tabular opacity-60'>{formatNumber(high)}<span className='text-[0.4em] font-semibold'> kr</span></p>
                        </div>
                    </div>

                    <p className='mt-10 max-w-lg text-xs text-tone-muted'>
                        Regneeksempel etter Svenns eget eksempel: 10 % av 8 mill. = 800 000 kr. Tallene sammenligner tilbud laget i Svenn Kalkulasjon med manuell prising, basert på Svenns kundedata.
                    </p>
                </div>
            </div>
        </section>
    )
}
